import { AddonType, FeatureKey } from '../../types/enums'

/**
 * How a feature is measured. Keeping this separate from the per-tier numbers is what lets
 * counts, periodic quotas and boolean flags share one resolution path, one usage table and
 * one error contract.
 */
export type FeatureKind =
  /** A cap on how many currently exist. Deleting frees the slot. */
  | 'count'
  /** A cap on how many were created within a period. Resets when the period rolls over. */
  | 'quota'
  /** On or off. */
  | 'boolean'

export type QuotaPeriod = 'calendar_month'

/**
 * Every feature's label key, derived from the feature itself. Typing it this way means a new
 * FeatureKey is a compile error in the translation map until it is translated.
 */
export type FeatureLabelKey = `plan.feature.${FeatureKey}`

export interface FeatureDefinition {
  kind: FeatureKind
  /** Only meaningful for `quota` features. */
  period?: QuotaPeriod
  /** Which add-on, if any, raises this feature's cap. */
  addon?: AddonType
  /** Translation key. Resolve it in the presentation layer - no locale strings live here. */
  labelKey: FeatureLabelKey
}

export const FEATURE_DEFINITIONS: Record<FeatureKey, FeatureDefinition> = {
  [FeatureKey.Customers]: { kind: 'count', labelKey: 'plan.feature.customers' },
  [FeatureKey.ReservationsPerMonth]: {
    kind: 'quota',
    period: 'calendar_month',
    labelKey: 'plan.feature.reservations_per_month',
  },
  [FeatureKey.Employees]: {
    kind: 'count',
    addon: AddonType.EmployeeSeat,
    labelKey: 'plan.feature.employees',
  },
  [FeatureKey.Places]: { kind: 'count', labelKey: 'plan.feature.places' },
  [FeatureKey.Activities]: { kind: 'count', labelKey: 'plan.feature.activities' },
  [FeatureKey.EventBlueprints]: { kind: 'count', labelKey: 'plan.feature.event_blueprints' },
  [FeatureKey.Announcements]: { kind: 'boolean', labelKey: 'plan.feature.announcements' },
  [FeatureKey.DataExport]: { kind: 'boolean', labelKey: 'plan.feature.data_export' },
  [FeatureKey.WebElements]: { kind: 'boolean', labelKey: 'plan.feature.web_elements' },
  [FeatureKey.SmsNotifications]: { kind: 'boolean', labelKey: 'plan.feature.sms_notifications' },
}

export const isNumericFeature = (feature: FeatureKey): boolean => FEATURE_DEFINITIONS[feature].kind !== 'boolean'
